import React, {Component} from 'react';
import styled from 'styled-components';
import {Grid, Row, Col as UnstyledCol} from 'react-flexbox-grid';
import {SortableElement, arrayMove} from 'react-sortable-hoc';
import sortableContainer from '../../components/sortableContainer';
import {NotificationManager} from 'react-notifications';
import {Link} from 'react-router-dom';
import {isQuestionDeleted} from "../FormBuilderScreen/shared";

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import Content from '../../components/Content';
import Loading from '../../components/Loading';
import QuestionChart from '../../components/QuestionChart';
import ContextWidget from '../../components/QuestionChart/ContextWidget';
import TemplateWidget from '../../components/TemplateWidget';
import Button from '../../components/Button';
import Menu from '../../components/Menu';
import Icon from '../../components/Icon';
import SubHeader from '../../components/SubHeader';
import HiddenWidgetsFormModal from './HiddenWidgetsFormModal'

import WidgetFormModal from './WidgetFormModal';

const today = new Date();
const oneDay = 1000 * 60 * 60 * 24;
const EMPTY_ARRAY = [];

class SheetDashboardTab extends Component {
  state = {
    loading: true,
    openPopOver: false,
    exporting: false,
    exportTemplate: false,
    startDate: new Date(today.getTime() - 7 * oneDay),
    endDate: today,
    widget: null,
    displayHiddenWidgetModel: false,
    addWidgetOptionsOpen: false,
    openWidgetForm: false
  };

  async componentDidMount() {
    const {template, sheets, rows, pinnedWidgets} = this.props;
    const worker = new Worker('/workers/sheet-dashboard-worker.js');
    this.setState ({pinnedWidgets: pinnedWidgets});
    // Invoke the worker.
    // Invoke the worker. A sheet added while it works is merged in once it is done.
    worker.onmessage = e => this.setState(e.data, this.mergeNewSheets);
    worker.postMessage([template, sheets, rows]);
  }

  componentDidUpdate(prevProps) {
    if (this.props.sheets !== prevProps.sheets) {
      this.mergeNewSheets();
    }
  }

  // Sheets being worked out to merge in, so a sheet is not merged twice while its worker runs.
  mergingSheetIds = new Set();

  // Works out the sheets the screen has taken in since the dashboard loaded and merges them into what is shown. Only
  // these are worked out: working everything out again would throw away answers edited on this tab, which are only
  // kept in our own state.
  mergeNewSheets = () => {
    const {template, sheets, rows} = this.props;
    const {loading, sheetsById} = this.state;

    // Still working out the first load, which calls back here when it is done.
    if (loading || !sheetsById) {
      return;
    }

    const newSheets = sheets.filter(sheet => !sheetsById[sheet.id] && !this.mergingSheetIds.has(sheet.id));

    if (newSheets.length < 1) {
      return;
    }

    const newRows = newSheets.reduce((map, sheet) => {
      this.mergingSheetIds.add(sheet.id);
      map[sheet.id] = rows[sheet.id] || EMPTY_ARRAY;
      return map;
    }, {});
    const worker = new Worker('/workers/sheet-dashboard-worker.js');

    worker.onmessage = e => {
      worker.terminate();
      newSheets.forEach(sheet => this.mergingSheetIds.delete(sheet.id));
      this.setState(prevState => mergeDashboardData(prevState, e.data));
    };
    worker.postMessage([template, newSheets, newRows]);
  }

  handleUpdateAnswers = async (sheet, questionId, answers) => {
    const {credentials} = this.props;
    const rollbackState = this.state;
    const answersWithMetadata = answers.map(answer => ({
      ...answer,
      dates: sheet.dates,
      sheetId: sheet.id
    }));

    this.setState(prevState => ({
      answersByQuestionId: {
        ...prevState.answersByQuestionId,
        [questionId]: answersWithMetadata
      },
      rows: prevState.rows.map(row => {
        if (row.sheetId !== sheet.id) {
          return row;
        }

        return {
          ...row,
          answers: [
            ...row.answers.filter(answer => answer.questionId !== questionId),
            ...answersWithMetadata
          ]
        }
      })
    }));

    try {
      await api.editAnswers(sheet.id, null, questionId, answers, null, credentials);
    }
    catch (e) {
      this.setState(rollbackState);
      NotificationManager.error('Your answers could not be saved! Please try again.');
    }
  }

  saveWidget = widget => {
    const {onTemplateChanged, template} = this.props;

    this.setState({widget: null});
    onTemplateChanged({
      ...template,
      widgets: [...template.widgets.filter(w => w.id !== widget.id), {...widget, isNewWidget: false}]
    }, () => {
      setTimeout(() => document.getElementById(widget.id).scrollIntoView({behavior: 'smooth'}), 250);
    });
  };

  deleteWidget = widget => {
    const {onTemplateChanged, template, credentials} = this.props;

    api.deleteWidget(template.id, widget.id, credentials);
    onTemplateChanged({
      ...template,
      widgets: template.widgets.filter(w => w.id !== widget.id)
    });
  }

  hideWidget = widgetToHide => {
    const {onTemplateChanged, template, credentials} = this.props;

    widgetToHide.widget.hidden = true;
    api.hideWidget(template.id, widgetToHide, credentials)
    onTemplateChanged({
      ...template,
      contextWidgetHidden: widgetToHide.widget.id === 'context-widget' ? true : template.contextWidgetHidden,
      questions: template.questions.map(q => ({...q, hidden: q.id === widgetToHide.widget.id ? true: q.hidden})),
      widgets: template.widgets.map(w => ({...w, hidden: w.id === widgetToHide.widget.id ? true : w.hidden}))
    });
  }

  pinWidget = async widgetToPin => {
    const {credentials, sheets, filters} = this.props;

    const sheetIds = sheets.map(sheet => {
      return sheet.id;
    })
    try {
      const response = await api.pinWidget(widgetToPin, credentials, sheetIds, filters);
      if(response === 429) {
        NotificationManager.error('Pinned widget limit exceeded.');
      } else {
        NotificationManager.success(`Widget was successfully pinned.`)
      }
    }
    catch (e) {
      NotificationManager.error('Error pinning widget.');
    }
  }

  unPinWidget = widgetToPin => {
    const {credentials} = this.props;
    widgetToPin.pinned = false;
    api.unPinWidget(widgetToPin, credentials)
  }

  handleUnhideWidgets = widgets => {
    const {onTemplateChanged, template, credentials} = this.props;

    api.unhideWidgets(template.id, widgets, credentials);
    onTemplateChanged({
      ...template,
      contextWidgetHidden: widgets.includes(template.id) ? false : template.contextWidgetHidden,
      questions: template.questions.map(q => ({...q, hidden: widgets.includes(q.id) ? false : q.hidden})),
      widgets: template.widgets.map(w => ({...w, hidden: widgets.includes(w.id) ? false : w.hidden}))
    });
  }

  handleEditWidget = widget => {
    this.setState({widget});
  }

  handleSortWidgets = async ({oldIndex, newIndex}) => {
    const {onTemplateChanged, template, credentials} = this.props;
    const widgets = arrayMove(this.getWidgets(), oldIndex, newIndex).map((widget, order) => ({...widget, order}));
    const sortOrderById = widgets.reduce((map, widget) => {
      map[widget.id] = widget.order;
      return map;
    }, {});

    onTemplateChanged({
      ...template,
      contextWidgetOrder: sortOrderById['context-widget'],
      questions: template.questions.map(q => ({...q, widgetOrder: sortOrderById[q.id]})),
      widgets: template.widgets.map(w => ({...w, widgetOrder: sortOrderById[w.id]}))
    });

    // Save new widget orders to the API.
    const request = {
      contextWidgetOrder: sortOrderById['context-widget'],
      questions: template.questions.reduce((map, question) => {
        map[question.id] = sortOrderById[question.id];
        return map;
      }, {}),
      templateWidgets: template.widgets.reduce((map, widget) => {
        map[widget.id] = sortOrderById[widget.id];
        return map;
      }, {})
    };

    api.sortWidgets(template.id, request, credentials);
  }

  async handleDownloadAllImages(question) {
    const {credentials, template, sheets} = this.props;

    try {
      const url = await api.getBulkDownloadImagesUrl(template.id, question.id, sheets.map(sheet => sheet.id), credentials);
      window.location = url;
    }
    catch (e) {
      console.error(e);
      NotificationManager.error('An error occurred while trying to download the images. Please try again.');
    }
  }

  getQuestionWidgetOptions(question) {
    if (question.type === 'SIGNATURE' || question.type === 'IMAGE') {
      return [
        {title: 'Download all images', onClick: () => this.handleDownloadAllImages(question)}
      ];
    }
  }

  getWidgets() {
    const {template, template: {contextQuestions, questions, widgets}, catalog, user, showDeleted, credentials, filters} = this.props;
    const {sheetsById, rows, answersByQuestionId} = this.state;
    const questionsById = getQuestionsById(template);

    return [
      {
        type: ContextWidget,
        id: 'context-widget',
        order: template.contextWidgetOrder || -1,
        hidden: template.contextWidgetHidden,
        sheetsById,
        template,
        catalog,
        questions: contextQuestions,
        published: template.published,
        answers: contextQuestions.map(question => answersByQuestionId[question.id] || EMPTY_ARRAY),
        onAnswersChanged: this.handleUpdateAnswers,
        onHide: this.hideWidget
      },
      ...widgets.map(widget => ({
        type: TemplateWidget,
        id: widget.id,
        order: widget.widgetOrder,
        hidden: widget.hidden,
        sheetsById,
        rows,
        widget,
        catalog,
        questions: questionsById,
        user: user,
        // Recent submissions widgets keep themselves up to date by polling the API for new rows.
        credentials,
        filters,
        onEdit: this.handleEditWidget,
        onDelete: this.deleteWidget,
        onHide: this.hideWidget,
        onPin: this.pinWidget,
        unPin: this.unPinWidget
      })),
      ...questions.map(question => ({
        type: QuestionChart,
        id: question.id,
        order: question.widgetOrder,
        hidden: question.hidden || (!showDeleted && isQuestionDeleted(question)), // hide widgets when question is deleted
        pinned: question.pinned,
        question,
        sheetsById,
        catalog,
        published: template.published,
        answers: answersByQuestionId[question.id] || EMPTY_ARRAY,
        options: this.getQuestionWidgetOptions(question),
        onHide: this.hideWidget,
        onPin: this.pinWidget,
        unPin: this.unPinWidget
      }))
    ].filter((widget) => !widget.hidden).sort((a, b) => a.order - b.order);
  }

  handleCreateWidget = () => {
    this.setState({widget: createWidget(this.getWidgets().length)});
  }

  toggleHiddenWidgetsForm = () => {
    this.setState(prevState => ({displayHiddenWidgetModel: !prevState.displayHiddenWidgetModel}));

  }

  toggleAddWidgetOptionsOpen = () => {
    this.setState(prevState => ({addWidgetOptionsOpen: !prevState.addWidgetOptionsOpen}));
  }

  handleCloseWidgetForm = () => {
    this.setState({widget: null});
  }

  render() {
    const {sheets, template, catalog, user} = this.props;
    const {loading, widget, rows, displayHiddenWidgetModel, addWidgetOptionsOpen} = this.state;

    if (loading) {
      return <Loading message="Loading dashboard..." />;
    }

    // Set up the list of widgets to be rendered.
    const items = this.getWidgets()
    // Only show the ones that still exist (aka question not deleted) and remove it from the count
    const hiddenWidgetCount = ((template.questions.filter(q=> !isQuestionDeleted(q)).length + template.widgets.length + 1) - items.length)
    return (
      <>
        {template.published && user.role !== "USER" &&
        <Toolbar>
          <Button id="add-widget-options" onClick={this.toggleAddWidgetOptionsOpen}>
            Add widget <Icon name="chevron-down" />
          </Button>
          <Menu
            open={addWidgetOptionsOpen} 
            //position="bottom-end"  
            offsetY={5} 
            relativeTo="add-widget-options" 
            onClose={this.toggleAddWidgetOptionsOpen}>
            <Menu.Section>
              <Menu.Option onClick={this.handleCreateWidget}>Create Widget</Menu.Option>
              <Menu.Option onClick={this.toggleHiddenWidgetsForm}>View Hidden ({hiddenWidgetCount > 0 ? hiddenWidgetCount : 0})</Menu.Option>
            </Menu.Section>
          </Menu>
        </Toolbar>}

        {items.length === 0 ?
          <NoWidgets>
            <Message>
              <img src="/assets/noWidgets.svg" alt="" width="290" />
                <SubHeader center>You have no visible widgets</SubHeader>
              <p>Select <Link onClick={this.toggleHiddenWidgetsForm}>View Hidden </Link>to see or unhide your hidden widgets.</p>
            </Message>
          </NoWidgets> :null}

        <Content>
          <Widgets
            useDragHandle
            distance={5}
            axis="xy"
            list={items}
            onSortEnd={this.handleSortWidgets}
          />
        </Content>
        <WidgetFormModal
          open={widget}
          template={template}
          sheets={sheets}
          rows={rows}
          catalog={catalog}
          widget={widget}
          onClose={this.handleCloseWidgetForm}
          onSave={this.saveWidget}
          user={user}
        />
        <HiddenWidgetsFormModal
          open={displayHiddenWidgetModel}
          template={template}
          onClose={this.toggleHiddenWidgetsForm}
          onSave={this.handleUnhideWidgets}
        />
      </>
    );
  }
}

const Widgets = sortableContainer(({list}) => (
  <Grid fluid>
    <Row>
      {list.map((item, index) => (
        <Widget {...item} key={item.id} index={index} />
      ))}
    </Row>
  </Grid>
));

const Widget = SortableElement(({type: WidgetType, id, ...props}) => (
  <Col xs={12} sm={12} md={12} lg={6}>
    <div id={id}>
      <WidgetType {...props} />
    </div>
  </Col>
));

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1.875rem;
  margin-bottom: 0.5rem;
`;

function createWidget(order) {
  return {
    isNewWidget: true,
    title: '',
    filter: null,
    map: null,
    aggregation: null,
    groupByQuestion: null,
    widgetOrder: order + 1,
    hidden: false
  };
}

function getQuestionsById(template) {
  return template.contextQuestions.concat(template.questions)
    .reduce((map, question) => {
      map[question.id] = question;
      return map;
    }, {});
}

function sortByLastModified(a, b) {
  return b.dates.lastModified - a.dates.lastModified;
}

// Merges what the worker worked out for some new sheets into what the dashboard already shows, sorted the same way the
// worker sorts.
function mergeDashboardData(state, added) {
  const answersByQuestionId = {...state.answersByQuestionId};

  Object.keys(added.answersByQuestionId).forEach(questionId => {
    answersByQuestionId[questionId] = (answersByQuestionId[questionId] || EMPTY_ARRAY)
      .concat(added.answersByQuestionId[questionId])
      .sort(sortByLastModified);
  });

  return {
    sheetsById: {...state.sheetsById, ...added.sheetsById},
    rows: state.rows.concat(added.rows).sort(sortByLastModified),
    answersByQuestionId
  };
}

const Col = styled(UnstyledCol)`
  padding: 0.9375rem !important;
`;

const NoWidgets = styled.div`
  height: calc(100vh - 18rem);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Message = styled.div`
  img {
    display: block;
    margin: 0 auto 2rem auto;
  }

  p {
    color: #747474;
    margin-top: 1rem;
    line-height: 1.5;
  }
`;

export default withUser(SheetDashboardTab);
